import type { GetServerSidePropsContext, NextPageContext } from 'next'
import type { NextRouter } from 'next/router'
import Cookie, { ICookie } from 'cookie-universal'
import uaParser from 'ua-parser-js'
import { isDevelopmentEnv, isStagingEnv } from '@mc/client-env'
import { getAnonymousId } from '@mc/next-anonymous-id'
import { initGeoService } from '@mc/next-geo'
import { getSessionDeviceId } from './adapters'

export const VALID_TEST_COOKIE_PATTERN = /^optly_[\w-]{2,14}?_test$/

const TEST_USER_PARAM = 'mc_qa_test_user'
const TEST_USER_COOKIE = 'optly_mc_qa_test'
const TEST_USER_COOKIE_MAX_AGE = 60 * 60 * 24

type Router = Pick<NextRouter, 'query'>
type Context = Pick<
  GetServerSidePropsContext | NextPageContext,
  'req' | 'res' | 'query'
>

export const getUserAttributes = (ctx: Context, router: Router) => {
  const { req, res } = ctx

  const { geo, region } = initGeoService(req, res, router)
  const ua = uaParser(req?.headers?.['user-agent'])
  const userId = getAnonymousId(req, res)
  const cookies = Cookie(req, res)

  return {
    bogo_available: cookies.get('bogo_available') || false,
    business_package: false,
    country: geo,
    device: ua.device.type || 'desktop',
    free_trial_type: getFreeTrialType(req, cookies),
    is_test_user: getIsTestUser(ctx, router, cookies),
    is_logged_in: cookies.get('_mcl') === 1,
    membership: getUserMembership(ctx),
    guest_pass_feature_available:
      cookies.get('guest_pass_feature_available') || false,
    partner_subscription: false,
    platform_name: 'web',
    promo_code: getPromoCode(ctx, cookies),
    region,
    saml: false,
    subscribed_through_bulk_gift: false,
    experience: 'consumer',
    app_version_code: null,
    userId,
    subscription_provider: null,
    session_device_id: getSessionDeviceId(cookies),
  }
}

const getFreeTrialType = (
  req: Context['req'],
  cookies: ICookie,
): 'guest_pass' | null => {
  const referralParam = (req as { params?: { referral?: string } })?.params
    ?.referral
  if (!!referralParam || cookies.get('referral_token')) {
    return 'guest_pass'
  }
  return null
}

const getPromoCode = (ctx: Context, cookies: ICookie): string | null =>
  (ctx.query?.promo_id as string | undefined) ||
  cookies.get('discount_slug') ||
  cookies.get('promo_code') ||
  null

// The cookie that the optimizely extension sets is based off of the domain. So rather than having folx tinker with
// their cookies, we can just check for the cookie set by the extension. The cookie the extension sets follows an
// "optly_[somthing]_test" pattern.
const getOptimizelyTestCookie = (cookies: ICookie): boolean =>
  Object.keys(cookies.getAll()).some((cookieName) =>
    cookieName.match(VALID_TEST_COOKIE_PATTERN),
  )

const getIsTestUser = (
  ctx: Context,
  router: Router,
  cookies: ICookie,
): boolean => {
  if (
    (isStagingEnv() || isDevelopmentEnv()) &&
    (router.query ?? ctx.query)?.[TEST_USER_PARAM] === 'true'
  ) {
    cookies.set(TEST_USER_COOKIE, 'true', {
      path: '/',
      maxAge: TEST_USER_COOKIE_MAX_AGE,
      secure: true,
    })

    return true
  }

  return getOptimizelyTestCookie(cookies)
}

export const getUserMembership = (ctx: Context): string => {
  const cookies = Cookie(ctx.req, ctx.res)
  return cookies.get('user_membership') || 'FREE'
}
